import React, { useEffect, useState, useRef } from 'react';
import { Button } from '../ui/Button';
import { ArrowRight, CheckCircle2 } from 'lucide-react';
import { motion, useInView, useAnimation } from 'framer-motion';
const Counter = ({
  from,
  to,
  duration = 2,
  decimals = 0,
  suffix = ''
}) => {
  const [count, setCount] = useState(from);
  const nodeRef = useRef(null);
  const inView = useInView(nodeRef, {
    once: true
  });
  useEffect(() => {
    if (inView) {
      let startTime;
      let animationFrame;
      const step = timestamp => {
        if (!startTime) startTime = timestamp;
        const progress = Math.min((timestamp - startTime) / (duration * 1000), 1);
        // Easing function for smooth deceleration
        const easeOutQuart = 1 - Math.pow(1 - progress, 4);
        const currentCount = from + (to - from) * easeOutQuart;
        setCount(currentCount);
        if (progress < 1) {
          animationFrame = requestAnimationFrame(step);
        }
      };
      animationFrame = requestAnimationFrame(step);
      return () => cancelAnimationFrame(animationFrame);
    }
  }, [inView, from, to, duration]);
  return <span ref={nodeRef} data-id="element-88">
      {count.toFixed(decimals)}
      {suffix}
    </span>;
};
export function Hero() {
  return <section className="relative bg-brand-dark text-white pt-12 pb-0 overflow-hidden" data-id="element-89">
      {/* Background Pattern & Particles */}
      <div className="absolute inset-0 opacity-10 pointer-events-none" data-id="element-90">
        <svg className="h-full w-full" viewBox="0 0 100 100" preserveAspectRatio="none" data-id="element-91">
          <path d="M0 100 C 20 0 50 0 100 100 Z" fill="white" data-id="element-92" />
        </svg>
      </div>

      {/* Floating Particles */}
      {[...Array(5)].map((_, i) => <motion.div key={i} className="absolute rounded-full bg-brand-gold/10 blur-xl" style={{
      width: Math.random() * 100 + 50,
      height: Math.random() * 100 + 50,
      left: `${Math.random() * 100}%`,
      top: `${Math.random() * 100}%`
    }} animate={{
      y: [0, -20, 0],
      opacity: [0.3, 0.6, 0.3],
      scale: [1, 1.1, 1]
    }} transition={{
      duration: Math.random() * 5 + 5,
      repeat: Infinity,
      ease: 'easeInOut'
    }} data-id="element-93" />)}

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 pb-20" data-id="element-94">
        <div className="text-center mb-12" data-id="element-95">
          <motion.div initial={{
          opacity: 0,
          y: -20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6
        }} className="inline-block bg-brand-gold/20 text-brand-gold px-4 py-1.5 rounded-full text-sm font-semibold mb-6 border border-brand-gold/30" data-id="element-96">
            PARA MUJERES DE +40 AÑOS
          </motion.div>

          <motion.h1 initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6,
          delay: 0.2
        }} className="font-serif text-4xl md:text-6xl lg:text-7xl font-bold leading-tight mb-6 text-brand-cream" data-id="element-97">
            No es grasa, es{' '}
            <span className="text-brand-gold italic" data-id="element-98">INFLAMACIÓN</span>
          </motion.h1>

          <motion.p initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} transition={{
          duration: 0.6,
          delay: 0.4
        }} className="text-xl md:text-2xl text-gray-300 max-w-3xl mx-auto font-light leading-relaxed" data-id="element-99">
            El protocolo de 7 días para "apagar el incendio" en tu abdomen y
            recuperar tu cintura sin dietas extremas.
          </motion.p>
        </div>

        <motion.div initial={{
        opacity: 0,
        y: 30
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.8,
        delay: 0.6
      }} className="grid md:grid-cols-2 gap-12 items-center" data-id="element-100">
          <div className="order-2 md:order-1" data-id="element-101">
            <div className="bg-white/5 backdrop-blur-sm rounded-2xl p-8 border border-white/10" data-id="element-102">
              <h3 className="text-2xl font-serif text-brand-gold mb-6" data-id="element-103">
                Lo que conseguirás en 7 días:
              </h3>
              <ul className="space-y-4 mb-8" data-id="element-104">
                {['Amanecer con el abdomen plano (sin pasar hambre)', 'Recuperar tu energía antes del próximo domingo', "Apagar el 'incendio' digestivo que te hace sentir pesada", 'Resetear tu metabolismo después de los 40'].map((item, i) => <motion.li key={i} initial={{
                opacity: 0,
                x: -10
              }} animate={{
                opacity: 1,
                x: 0
              }} transition={{
                delay: 0.8 + i * 0.1
              }} className="flex items-start" data-id="element-105">
                    <CheckCircle2 className="h-6 w-6 text-brand-gold mr-3 flex-shrink-0" data-id="element-106" />
                    <span className="text-lg text-gray-200" data-id="element-107">{item}</span>
                  </motion.li>)}
              </ul>
              <div className="flex flex-col sm:flex-row gap-4" data-id="element-108">
                <motion.div className="w-full" whileHover={{
                scale: 1.02
              }} whileTap={{
                scale: 0.98
              }} data-id="element-109">
                  {/* Modified section: restructuring Button component as per comment */}
                  <Button variant="primary" size="lg" className="w-full text-brand-dark font-bold text-lg shadow-brand-gold/20 shadow-xl relative overflow-hidden group" onClick={() => document.getElementById('offer')?.scrollIntoView({
                  behavior: 'smooth'
                })} data-id="element-110">
                    <motion.span className="absolute inset-0 rounded-md" animate={{
                    boxShadow: ['0 0 20px rgba(212,175,55,0.3)', '0 0 40px rgba(212,175,55,0.6)', '0 0 20px rgba(212,175,55,0.3)']
                  }} transition={{
                    repeat: Infinity,
                    duration: 2
                  }} data-id="element-111" />
                    <span className="relative flex items-center justify-center" data-id="element-112">
                      SÍ, QUIERO MI RESET AHORA
                      <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" data-id="element-113" />
                    </span>
                  </Button>
                </motion.div>
              </div>
              <p className="mt-4 text-center text-sm text-gray-400" data-id="element-114">
                <span className="text-brand-gold" data-id="element-115">Oferta Especial:</span> $6.36
                USD{' '}
                <span className="line-through text-gray-500" data-id="element-116">Antes $148</span>
              </p>
            </div>
          </div>

          <div className="order-1 md:order-2 flex justify-center relative" data-id="element-117">
            <motion.div animate={{
            scale: [0.95, 1.05, 0.95],
            opacity: [0.5, 0.8, 0.5]
          }} transition={{
            duration: 4,
            repeat: Infinity,
            ease: 'easeInOut'
          }} className="absolute inset-0 bg-brand-gold/20 blur-3xl rounded-full transform scale-75" data-id="element-118"></motion.div>
            <motion.img initial={{
            opacity: 0,
            scale: 0.9,
            rotate: -2
          }} animate={{
            opacity: 1,
            scale: 1,
            rotate: 0
          }} transition={{
            duration: 0.8,
            delay: 0.4
          }} whileHover={{
            scale: 1.05,
            rotate: 0
          }} src="https://cdn.magicpatterns.com/uploads/cHYNMMDpLRiGB4gVjUcqa7/heylloyd-image-1771657523791.png" alt="Reset Metabólico 7 Días - Guía Digital en iPad" className="relative z-10 w-full max-w-md drop-shadow-2xl" data-id="element-119" />
          </div>
        </motion.div>
      </div>

      {/* Social Proof Bar */}
      <div className="bg-brand-dark border-t border-brand-gold/20 py-6 relative z-20" data-id="element-120">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-121">
          <div className="flex flex-col md:flex-row justify-center items-center gap-6 md:gap-12 text-center" data-id="element-122">
            <div className="flex items-center text-brand-gold font-bold text-lg" data-id="element-123">
              +<Counter from={0} to={2847} duration={2.5} data-id="element-124" /> Mujeres
              Transformadas
            </div>
            <div className="hidden md:block w-px h-8 bg-brand-gold/20" data-id="element-125"></div>
            <div className="flex items-center text-brand-gold font-bold text-lg" data-id="element-126">
              <Counter from={0} to={4.9} decimals={1} duration={2} suffix="/5" data-id="element-127" />{' '}
              Estrellas
            </div>
            <div className="hidden md:block w-px h-8 bg-brand-gold/20" data-id="element-128"></div>
            <div className="flex items-center text-brand-gold font-bold text-lg" data-id="element-129">
              <Counter from={0} to={97} duration={2} suffix="%" data-id="element-130" /> Tasa de Éxito
            </div>
          </div>
        </div>
      </div>
    </section>;
}